/* eslint-disable i18next/no-literal-string */
import VehicleMarker from "./VehicleMarker";
import LottieAnimation from "./LottieAnimation";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why. screenMask has no AVIF/WebP siblings:
// its alpha channel IS the mask shape (never painted), and image-set() support in
// mask-image is patchier than in background-image, so it stays a single PNG.
const phoneBezel = imageUrl("/images/landing/location-phone.png");
const mapGeneric = imageUrl("/images/landing/location-map.jpg");
const screenMask = imageUrl("/images/landing/location-screen-mask.png");

/**
 * Ported from bouncieweb's `LandingPageComponents/HighlightAnimations/LocationAnimation.js`.
 *
 * A phone bezel with a map that slowly pans (skew + translate keyframes), a
 * vehicle marker that drifts across the screen and rotates near the end, and a
 * lottie trip-path overlay that draws once. Everything is masked to the phone
 * screen shape via the `location-screen-mask.png` mask image.
 *
 * All keyframes only run once the wrapper is on screen (`animate`), driven from
 * the parent's `useHasBeenOnScreen`. Keyframes are scoped/uniquely named in a
 * local <style>. Only the non-fleet (`isFleet=false`) assets are ported since
 * none of the ported landing pages use the fleet variant.
 */
export default function LocationAnimation({ animate }: { animate: boolean }) {
  return (
    <>
      <style>{`
        @keyframes cf-location-wrapper {
          0%   { transform: translate(0px, 30px); }
          100% { transform: translate(0px, 0px); }
        }
        @keyframes cf-location-map {
          0%   { transform: skew(0.5deg) translate(0%, 0%); }
          30%  { transform: skew(0.5deg) translate(1.5%, 3%); }
          100% { transform: skew(1deg) translate(7%, 3%); }
        }
        @keyframes cf-location-marker-pos {
          0%   { transform: translate(0%, 0%); }
          30%  { transform: translate(-30%, -30%); }
          100% { transform: translate(-30%, -35%); }
        }
        @keyframes cf-location-marker-rotate {
          0%   { transform: rotateY(0deg) rotateX(50deg) rotateZ(-32.5deg); }
          100% { transform: rotateY(0deg) rotateX(50deg) rotateZ(-90deg); }
        }
      `}</style>

      {/* LocationAnimationWrapper */}
      <div
        className={cnWrapper()}
        style={{
          aspectRatio: "1 / 1",
          transform: "translate(0px, 30px)",
          animationTimingFunction: "ease-out",
          animationFillMode: "forwards",
          animationIterationCount: 1,
          animationName: animate ? "cf-location-wrapper" : undefined,
          animationDuration: animate ? "3s" : undefined,
        }}
      >
        {/* MapMask — clips the map to the phone screen shape */}
        <div
          className="absolute left-[-0.1%] h-[87%] w-full max-w-none"
          style={{
            maskImage: `url(${screenMask})`,
            WebkitMaskImage: `url(${screenMask})`,
            maskSize: "cover",
            WebkitMaskSize: "cover",
          }}
        >
          {/* Map */}
          <Picture
            src={mapGeneric}
            alt=""
            className="absolute h-fit max-w-none"
            style={{
              width: "130%",
              top: "-5%",
              left: "-15.5%",
              transform: "skew(-0.5deg)",
              animationTimingFunction: animate ? "linear" : "ease-out",
              animationFillMode: "forwards",
              animationIterationCount: 1,
              animationName: animate ? "cf-location-map" : undefined,
              animationDuration: animate ? "7.25s" : undefined,
            }}
          />
        </div>

        {/* Phone bezel */}
        <Picture
          src={phoneBezel}
          alt=""
          className="absolute h-fit w-full max-w-none"
        />

        {/* Lottie trip-path overlay — only mounts (and plays) once on screen */}
        {animate && (
          <LottieAnimation
            path="/landing/location-animation.json"
            loop={false}
            styles={{
              position: "absolute",
              width: "100%",
              top: 0,
              left: 0,
            }}
          />
        )}

        {/* VehicleMarkerPositioner */}
        <div
          className="absolute"
          style={{
            top: "23.5%",
            left: "45.5%",
            width: "11%",
            height: "11%",
            transform: "translate(100%, 31%)",
            animationTimingFunction: animate ? "linear" : "ease-out",
            animationFillMode: "forwards",
            animationIterationCount: 1,
            animationName: animate ? "cf-location-marker-pos" : undefined,
            animationDuration: animate ? "6s" : undefined,
          }}
        >
          <VehicleMarker
            size="100%"
            styles={{
              transform: "rotateY(0deg) rotateX(50deg) rotateZ(-32.5deg)",
              animationTimingFunction: "linear",
              animationFillMode: "forwards",
              animationIterationCount: 1,
              animationName: animate ? "cf-location-marker-rotate" : undefined,
              animationDuration: "0.4s",
              animationDelay: "1.9s",
            }}
          />
        </div>
      </div>
    </>
  );
}

/**
 * Responsive positioning for LocationAnimationWrapper.
 * base: top 6.5rem, left -5%, width 110%
 * xsm(431): left 20%, width 60%   sm(640): left 10%, width 80%
 * md(768): left 5%, width 90%     lg(1024): top 9rem, left 5%, width 90%
 */
function cnWrapper() {
  return [
    "pointer-events-none absolute h-auto",
    "top-[6.5rem] left-[-5%] w-[110%]",
    "min-[431px]:left-[20%] min-[431px]:w-[60%]",
    "sm:left-[10%] sm:w-[80%]",
    "md:left-[5%] md:w-[90%]",
    "lg:top-[9rem] lg:left-[5%] lg:w-[90%]",
  ].join(" ");
}
